import * as Sentry from "@sentry/browser";
import type { JsonDataWithFields } from "./bindings/json_line_data";

export class dataFetcher {
	fetchedDataRanges: {
		[key: string]: { startDate: Date; endDate: Date; data: JsonDataWithFields };
	};

	constructor() {
		this.fetchedDataRanges = {};
	}

	async fetchData(
		activeStation: string | null,
		start: string,
		end: string | null,
	): Promise<JsonDataWithFields> {
		if (!start) {
			console.error("No start date set for fetch");
			return { fields: {}, data: {} };
		}
		const startDate = new Date(start);
		const now = new Date();
		// make sure the end date is not in the future, to prevent 'future' caching issues
		const endDate = end && new Date(end) < now ? new Date(end) : now;
		let queryString = "";

		if (!activeStation || activeStation === "all") {
			activeStation = "all";
		} else {
			queryString += `station_id=${activeStation}&`;
		}

		if (!this.fetchedDataRanges[activeStation]) {
			this.fetchedDataRanges[activeStation] = {
				startDate: now,
				endDate: new Date(1990, 0, 1),
				data: { fields: {}, data: {} },
			};
		}
		const storedRange = this.fetchedDataRanges[activeStation];

		// Check if we need to fetch new data (date range extends beyond cached range)
		const cacheHit = !(
			startDate < storedRange.startDate || endDate > storedRange.endDate
		);
		Sentry.logger.debug(
			Sentry.logger
				.fmt`fetchData cache ${cacheHit ? "HIT" : "MISS"} for station ${activeStation}`,
			{
				station: activeStation,
				cache_hit: cacheHit,
				requested_from: startDate.toISOString(),
				requested_to: endDate.toISOString(),
				cached_from: storedRange.startDate.toISOString(),
				cached_to: storedRange.endDate.toISOString(),
			},
		);

		if (!cacheHit) {
			queryString += `from_date=${startDate.toISOString()}&to_date=${endDate.toISOString()}`;
			const data = await Sentry.startSpan(
				{
					op: "http.client",
					name: "GET get_data/as_json",
					attributes: {
						station: activeStation,
						from_date: startDate.toISOString(),
						to_date: endDate.toISOString(),
						cache_hit: false,
					},
				},
				async (span) => {
					const response = await fetch(`get_data/as_json?${queryString}`);
					const json = (await response.json()) as JsonDataWithFields;
					// Count timestamps returned for the active station (field "0")
					const stationData = json.data[activeStation];
					const firstTable = stationData
						? Object.values(stationData)[0]
						: undefined;
					const pointCount = firstTable ? (firstTable["0"]?.length ?? 0) : 0;
					span.setAttribute("point_count", pointCount);
					Sentry.logger.info(
						Sentry.logger
							.fmt`fetchData received ${pointCount} points for station ${activeStation}`,
						{ station: activeStation, point_count: pointCount },
					);
					return json;
				},
			);
			// Update cache with expanded date range
			this.fetchedDataRanges[activeStation] = {
				startDate,
				endDate,
				data,
			};
			return data;
		}

		// Return cached data
		return storedRange.data;
	}
}
